import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  deleteDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  onSnapshot,
  increment,
  writeBatch,
  Timestamp,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, auth } from '../firebase';
import { functions } from '../firebase';
import { clientKeyManager } from '../db/helpers/clientKeyManager';
import {
  Team,
  TeamPost,
  Follow,
  Comment,
  Like,
  Notification,
  TeamJoinRequest,
  VideoCategory,
  TeamVideo,
  ReactionCounts,
  DEFAULT_ROSTER,
} from '../../../common/src/types/user';
import { ReactionService } from './reactionService';
import {
  UserRef_Document,
  UsersRef_Collection,
  UserNotificationsRef_Collection,
  UserNotificationRef_Document,
  FollowsRef_Collection,
  FollowRef_Document,
  UserFollowingRef_Query,
  UserFollowersRef_Query,
  LikesRef_Collection,
  LikeRef_Document,
  CommentsRef_Collection,
  CommentRef_Document,
  SessionCommentsRef_Query,
  TeamPostsRef_Collection,
  TeamPostRef_Document,
  TeamPostsRef_Query,
  TeamsRef_Collection,
  TeamRef_Document,
  OrganizationTeamsRef_Query,
} from '../db';

// Helper to safely convert Firestore Timestamp or Date to Date
const toDate = (value: any): Date => {
  if (!value) return new Date();
  if (value instanceof Date) return value;
  if (value.toDate && typeof value.toDate === 'function') return value.toDate();
  return new Date();
};

export class SocialService {
  // Team Management
  static async createTeam(
    teamData: Omit<Team, 'id' | 'created_at' | 'updated_at'>,
    organizationId?: string,
    clientKey?: string
  ): Promise<Team> {
    try {
      clientKey = clientKey ?? await clientKeyManager.getClientKey();
      const currentUserId = auth.currentUser?.uid!;

      // Teams are at top-level: /clients/{clientKey}/teams/{teamId}
      const teamsCollection = TeamsRef_Collection(clientKey);
      const teamDocRef = doc(teamsCollection);
      const teamId = teamDocRef.id;

      const newTeamData = {
        ...teamData,
        id: teamId,
        organization_id: organizationId || null, // Optionally link to organization
        coach_id: currentUserId, // Legacy field for backward compatibility
        coach_ids: teamData.coach_ids || [currentUserId], // New array field
        invite_code: this.generateInviteCode(),
        // Every team starts with one roster so lineups/depth charts work on
        // day one (ROSTER_MODEL_SPEC R0)
        rosters: teamData.rosters?.length ? teamData.rosters : [DEFAULT_ROSTER],
        scope: organizationId ? 'organization' : teamData.scope || 'public', // Mark as organization team if linked
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      };

      await setDoc(teamDocRef, newTeamData);

      // Update team_ids for all coaches and members
      const allUserIds = new Set([
        ...newTeamData.coach_ids,
        ...(newTeamData.members || [])
      ]);

      for (const userId of allUserIds) {
        try {
          const userDocRef = UserRef_Document(userId, clientKey);
          await updateDoc(userDocRef, {
            team_ids: arrayUnion(teamId) // Add teamId to team_ids array
          });
        } catch (error) {          // Continue even if updating one user fails
        }
      }

      // Read back the document to get actual Firestore timestamps
      const docSnap = await getDoc(teamDocRef);
      if (!docSnap.exists()) {
        throw new Error('Team creation failed');
      }

      const data = docSnap.data();
      return {
        ...data,
        id: teamId,
        created_at: toDate(data.created_at),
        updated_at: toDate(data.updated_at),
      } as Team;
    } catch (error) {      throw new Error('Failed to create team');
    }
  }

  static async getTeam(teamId: string, clientKey?: string): Promise<Team | null> {
    try {
      clientKey = clientKey ?? await clientKeyManager.getClientKey();

      const teamDocRef = TeamRef_Document(teamId, clientKey);
      const docSnap = await getDoc(teamDocRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          ...data,
          id: docSnap.id,
          created_at: toDate(data.created_at),
          updated_at: toDate(data.updated_at),
        } as Team;
      }
      return null;
    } catch (error) {      throw new Error('Failed to get team');
    }
  }

  static async getCoachTeams(coachId: string, clientKey?: string): Promise<Team[]> {
    try {
      clientKey = clientKey ?? await clientKeyManager.getClientKey();

      const q = query(
        TeamsRef_Collection(clientKey),
        where('coach_ids', 'array-contains', coachId),
        orderBy('created_at', 'desc')
      );

      const querySnapshot = await getDocs(q);      return querySnapshot.docs.map((doc) => {
        const data = doc.data() as any;
        return {
          ...data,
          id: doc.id,
          created_at: toDate(data.created_at),
          updated_at: toDate(data.updated_at),
        } as Team;
      });
    } catch (error) {      throw new Error('Failed to get coach teams');
    }
  }

  static async getUserTeams(userId: string): Promise<Team[]> {
    try {
      // Get user's team_ids from their profile
      const userDoc = await getDoc(UserRef_Document(userId));      if (!userDoc.exists()) {        return [];
      }

      const userData = userDoc.data();
      const teamIds = userData.team_ids || [];
      if (teamIds.length === 0) {
        return [];
      }

      // Fetch all teams by ID
      const teams: Team[] = [];
      for (const teamId of teamIds) {
        try {

          const team = await this.getTeam(teamId);
          if (team) {
            teams.push(team);
          }
        } catch (error) {        }
      }      return teams;
    } catch (error) {      throw new Error('Failed to get user teams');
    }
  }

  static async updateTeam(teamId: string, updates: Partial<Team>, clientKey?: string): Promise<void> {
    try {
      clientKey = clientKey ?? await clientKeyManager.getClientKey();

      const teamRef = TeamRef_Document(teamId, clientKey);
      await updateDoc(teamRef, {
        ...updates,
        updated_at: serverTimestamp(),
      });
    } catch (error) {      throw new Error('Failed to update team');
    }
  }

  static async deleteTeam(teamId: string, clientKey?: string): Promise<void> {
    try {
      clientKey = clientKey ?? await clientKeyManager.getClientKey();

      const teamDocRef = TeamRef_Document(teamId, clientKey);
      await deleteDoc(teamDocRef);
    } catch (error) {      throw new Error('Failed to delete team');
    }
  }

  static async uploadTeamPhoto(file: File, teamId: string): Promise<string> {
    try {

      const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
      const { storage } = await import('../firebase');

      const photoRef = ref(storage, `teams/${teamId}/photo.jpg`);
      const snapshot = await uploadBytes(photoRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      // Update team with new photo URL
      await this.updateTeam(teamId, { photo_url: downloadURL });

      return downloadURL;
    } catch (error) {      throw new Error('Failed to upload team photo');
    }
  }

  static async joinTeam(teamId: string, userId: string): Promise<void> {
    try {

      const { arrayUnion } = await import('firebase/firestore');

      // Get team data to check if it's an organization team
      const team = await this.getTeam(teamId);
      if (!team) {
        throw new Error('Team not found');
      }

      // If team belongs to an organization, verify user is also in that organization
      if (team.organization_id) {
        // Get user's profile to check their organization
        const userDoc = await getDoc(UserRef_Document(userId));
        if (!userDoc.exists()) {
          throw new Error('User profile not found');
        }

        const userData = userDoc.data();
        const userOrgId = userData.organization_id;

        if (userOrgId !== team.organization_id) {
          throw new Error('This team belongs to a school. You must be a member of that school to join this team. Please contact your school administrator to request access.');
        }
      }

      // Find the team document using collection group query
      const q = query(
        TeamsRef_Collection(await clientKeyManager.getClientKey()),
        where('id', '==', teamId)
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        throw new Error('Team not found');
      }

      const teamRef = querySnapshot.docs[0].ref;

      // Update both the team's members array AND the user's team_ids array
      const { arrayUnion: arrayUnionImport } = await import('firebase/firestore');
      const batch = writeBatch(db);

      // Update team members
      batch.update(teamRef, {
        members: arrayUnionImport(userId),
        athlete_ids: arrayUnionImport(userId),
        updated_at: serverTimestamp(),
      });

      // Update user's team_ids
      const userRef = UserRef_Document(userId);
      batch.update(userRef, {
        team_ids: arrayUnionImport(team.id),
        updated_at: serverTimestamp(),
      });

      await batch.commit();
    } catch (error) {      throw error;
    }
  }

  static async leaveTeam(teamId: string, userId: string): Promise<void> {
    try {

      const { arrayRemove } = await import('firebase/firestore');

      // Find the team document using collection group query
      const q = query(
        TeamsRef_Collection(await clientKeyManager.getClientKey()),
        where('id', '==', teamId)
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        throw new Error('Team not found');
      }

      // Update both the team's members array AND the user's team_ids array
      const batch = writeBatch(db);
      const teamRef = querySnapshot.docs[0].ref;

      // Remove from team members
      batch.update(teamRef, {
        members: arrayRemove(userId),
        athlete_ids: arrayRemove(userId),
        updated_at: serverTimestamp(),
      });

      // Remove from user's team_ids
      const userRef = UserRef_Document(userId);
      batch.update(userRef, {
        team_ids: arrayRemove(teamId),
        updated_at: serverTimestamp(),
      });

      await batch.commit();
    } catch (error) {      throw new Error('Failed to leave team');
    }
  }

  // Team Join Request Management
  static async createJoinRequest(
    teamId: string,
    userId: string,
    userDisplayName: string,
    userPhotoURL?: string,
    clientKey?: string
  ): Promise<TeamJoinRequest> {
    try {
      void clientKey;
      const existingRequests = await this.getUserJoinRequests(userId);
      const hasPendingRequest = existingRequests.some(
        req => req.team_id === teamId && req.status === 'pending'
      );

      if (hasPendingRequest) {
        throw new Error('You already have a pending request for this team');
      }

      const requestTeamAccessCallable = httpsCallable(functions, 'requestTeamAccess');
      const result = await requestTeamAccessCallable({
        teamId,
        idempotencyKey: `team-${userId}-${teamId}-${Date.now()}`,
      });
      const data = result.data as { success: boolean; requestId: string; status: 'pending' | 'approved' | 'rejected' | 'cancelled' };

      const mappedStatus: TeamJoinRequest['status'] =
        data.status === 'approved' ? 'approved' : data.status === 'rejected' || data.status === 'cancelled' ? 'rejected' : 'pending';

      return {
        id: data.requestId,
        team_id: teamId,
        user_id: userId,
        user_display_name: userDisplayName,
        user_photo_url: userPhotoURL,
        status: mappedStatus,
        created_at: new Date(),
      };
    } catch (error) {      throw error;
    }
  }

  static async getTeamJoinRequests(teamId: string): Promise<TeamJoinRequest[]> {
    try {
      const clientKey = await clientKeyManager.getClientKey();
      const requestsRef = collection(db, 'clients', clientKey, 'team_access_requests');

      const q = query(
        requestsRef,
        where('target_team_id', '==', teamId),
        where('status', '==', 'pending'),
        orderBy('created_at', 'desc')
      );

      const querySnapshot = await getDocs(q);
      const requests = await Promise.all(querySnapshot.docs.map(async (requestDoc) => {
        const data = requestDoc.data();
        const requesterId = data.requester_user_id as string;
        let displayName = 'User';
        let photoUrl: string | undefined;

        if (requesterId) {
          const userSnap = await getDoc(UserRef_Document(requesterId, clientKey));
          if (userSnap.exists()) {
            const userData = userSnap.data() as any;
            displayName = userData.display_name || userData.name || userData.email || displayName;
            photoUrl = userData.photo_url;
          }
        }

        return {
          id: requestDoc.id,
          team_id: (data.target_team_id as string) || teamId,
          user_id: requesterId,
          user_display_name: displayName,
          user_photo_url: photoUrl,
          status: 'pending',
          created_at: toDate(data.created_at),
          reviewed_at: data.reviewed_at ? toDate(data.reviewed_at) : undefined,
          reviewed_by: data.reviewed_by as string | undefined,
        } as TeamJoinRequest;
      }));

      return requests;
    } catch (error) {      throw new Error('Failed to get team join requests');
    }
  }

  static async getUserJoinRequests(userId: string): Promise<TeamJoinRequest[]> {
    try {
      const clientKey = await clientKeyManager.getClientKey();
      const requestsRef = collection(db, 'clients', clientKey, 'team_access_requests');

      const q = query(
        requestsRef,
        where('requester_user_id', '==', userId),
        orderBy('created_at', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((requestDoc) => {
        const data = requestDoc.data();
        const rawStatus = data.status as 'pending' | 'approved' | 'rejected' | 'cancelled';
        const status: TeamJoinRequest['status'] =
          rawStatus === 'approved' ? 'approved' : rawStatus === 'rejected' || rawStatus === 'cancelled' ? 'rejected' : 'pending';

        return {
          id: requestDoc.id,
          team_id: data.target_team_id,
          user_id: data.requester_user_id,
          user_display_name: data.requester_display_name || 'You',
          user_photo_url: data.requester_photo_url,
          status,
          created_at: toDate(data.created_at),
          reviewed_at: data.reviewed_at ? toDate(data.reviewed_at) : undefined,
          reviewed_by: data.reviewed_by,
        } as TeamJoinRequest;
      });
    } catch (error) {      throw new Error('Failed to get user join requests');
    }
  }

  static async approveJoinRequest(requestId: string, _reviewerId: string): Promise<void> {
    try {
      void _reviewerId;
      const clientKey = await clientKeyManager.getClientKey();
      const requestRef = doc(db, 'clients', clientKey, 'team_access_requests', requestId);
      const requestSnap = await getDoc(requestRef);
      const requestData = requestSnap.exists() ? requestSnap.data() : null;

      const approveCallable = httpsCallable(functions, 'approveTeamAccessRequest');
      await approveCallable({ requestId });

      // Create notification for user
      if (requestData?.requester_user_id && requestData?.target_team_id) {
        await this.createNotification({
          user_id: requestData.requester_user_id,
          type: 'team_invite',
          title: 'Join Request Approved',
          message: 'Your request to join the team has been approved!',
          read: false,
          related_id: requestData.target_team_id,
          related_type: 'team',
        });
      }
    } catch (error) {      throw error;
    }
  }

  static async rejectJoinRequest(requestId: string, _reviewerId: string): Promise<void> {
    try {
      void _reviewerId;
      const clientKey = await clientKeyManager.getClientKey();
      const requestRef = doc(db, 'clients', clientKey, 'team_access_requests', requestId);
      const requestSnap = await getDoc(requestRef);
      const requestData = requestSnap.exists() ? requestSnap.data() : null;

      const rejectCallable = httpsCallable(functions, 'rejectTeamAccessRequest');
      await rejectCallable({ requestId });

      // Create notification for user
      if (requestData?.requester_user_id && requestData?.target_team_id) {
        await this.createNotification({
          user_id: requestData.requester_user_id,
          type: 'team_invite',
          title: 'Join Request Declined',
          message: 'Your request to join the team was not approved at this time.',
          read: false,
          related_id: requestData.target_team_id,
          related_type: 'team',
        });
      }
    } catch (error) {      throw error;
    }
  }

  // Team Coach Management
  static async addCoach(teamId: string, coachId: string): Promise<void> {
    try {

      const { arrayUnion } = await import('firebase/firestore');
      const team = await this.getTeam(teamId);

      if (!team) {
        throw new Error('Team not found');
      }

      // Initialize coach_ids if it doesn't exist
      const currentCoachIds = team.coach_ids || [team.coach_id];

      if (currentCoachIds.includes(coachId)) {
        throw new Error('User is already a coach of this team');
      }

      // Find the team document using collection group query
      const q = query(
        TeamsRef_Collection(await clientKeyManager.getClientKey()),
        where('id', '==', teamId)
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        throw new Error('Team not found');
      }

      const teamRef = querySnapshot.docs[0].ref;
      await updateDoc(teamRef, {
        coach_ids: arrayUnion(coachId),
        updated_at: serverTimestamp(),
      });
    } catch (error) {      throw error;
    }
  }

  static async removeCoach(teamId: string, coachId: string): Promise<void> {
    try {

      const { arrayRemove } = await import('firebase/firestore');
      const team = await this.getTeam(teamId);

      if (!team) {
        throw new Error('Team not found');
      }

      const currentCoachIds = team.coach_ids || [team.coach_id];

      // Don't allow removing the last coach
      if (currentCoachIds.length <= 1) {
        throw new Error('Cannot remove the last coach from a team');
      }

      // Find the team document using collection group query
      const q = query(
        TeamsRef_Collection(await clientKeyManager.getClientKey()),
        where('id', '==', teamId)
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        throw new Error('Team not found');
      }

      const teamRef = querySnapshot.docs[0].ref;
      await updateDoc(teamRef, {
        coach_ids: arrayRemove(coachId),
        updated_at: serverTimestamp(),
      });
    } catch (error) {      throw error;
    }
  }

  // Organization Team Linking
  static async generateOrgLinkCode(teamId: string): Promise<string> {
    try {

      const team = await this.getTeam(teamId);

      if (!team) {
        throw new Error('Team not found');
      }

      // Check if team already has an org link code
      if (team.org_link_code) {
        return team.org_link_code;
      }

      const linkCode = this.generateInviteCode();

      // Find the team document using collection group query
      const q = query(
        TeamsRef_Collection(await clientKeyManager.getClientKey()),
        where('id', '==', teamId)
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        throw new Error('Team not found');
      }

      const teamRef = querySnapshot.docs[0].ref;
      await updateDoc(teamRef, {
        org_link_code: linkCode,
        updated_at: serverTimestamp(),
      });

      return linkCode;
    } catch (error) {      throw new Error('Failed to generate organization link code');
    }
  }

  static async linkTeamToOrganization(orgLinkCode: string, organizationId: string): Promise<Team> {
    try {

      // Find team by org link code
      const q = query(
        TeamsRef_Collection(await clientKeyManager.getClientKey()),
        where('org_link_code', '==', orgLinkCode)
      );

      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        throw new Error('Invalid organization link code');
      }

      const teamDoc = querySnapshot.docs[0];
      const team = { ...teamDoc.data(), id: teamDoc.id } as Team;

      // Check if team is already linked to an organization
      if (team.organization_id) {
        throw new Error('Team is already linked to an organization');
      }

      // Update team with organization ID and change scope (using existing teamDoc ref)
      const teamRef = teamDoc.ref;
      await updateDoc(teamRef, {
        organization_id: organizationId,
        scope: 'organization',
        updated_at: serverTimestamp(),
      });

      return { ...team, organization_id: organizationId, scope: 'organization' };
    } catch (error) {      throw error;
    }
  }

  static async unlinkTeamFromOrganization(teamId: string): Promise<void> {
    try {

      // Find the team document using collection group query
      const q = query(
        TeamsRef_Collection(await clientKeyManager.getClientKey()),
        where('id', '==', teamId)
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        throw new Error('Team not found');
      }

      const teamRef = querySnapshot.docs[0].ref;
      await updateDoc(teamRef, {
        organization_id: null,
        scope: 'public',
        updated_at: serverTimestamp(),
      });
    } catch (error) {      throw new Error('Failed to unlink team from organization');
    }
  }

  // Team Posts/Feed System moved to TeamPostService (services/teamPostService.ts)

  // Notifications System
  static async createNotification(notificationData: Omit<Notification, 'id' | 'created_at'>): Promise<void> {
    try {

      const userId = notificationData.user_id;
      const notificationId = doc(UserNotificationsRef_Collection(userId)).id;
      const newNotification: Notification = {
        ...notificationData,
        id: notificationId,
        created_at: new Date(),
      };

      await setDoc(UserNotificationRef_Document(userId, notificationId), {
        ...newNotification,
        created_at: serverTimestamp(),
      });
    } catch (error) {      throw new Error('Failed to create notification');
    }
  }

  static async getUserNotifications(userId: string, limitCount: number = 20): Promise<Notification[]> {
    try {

      const q = query(
        UserNotificationsRef_Collection(userId),
        orderBy('created_at', 'desc'),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(q);      return querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          ...data,
          id: doc.id,
          created_at: toDate(data.created_at),
        } as Notification;
      });
    } catch (error) {      throw new Error('Failed to get user notifications');
    }
  }

  static async markNotificationAsRead(userId: string, notificationId: string): Promise<void> {
    try {

      const notificationRef = UserNotificationRef_Document(userId, notificationId);
      await updateDoc(notificationRef, {
        read: true,
      });
    } catch (error) {      throw new Error('Failed to mark notification as read');
    }
  }

  static async markAllNotificationsAsRead(userId: string): Promise<void> {
    try {

      const q = query(
        UserNotificationsRef_Collection(userId),
        where('read', '==', false)
      );

      const querySnapshot = await getDocs(q);
      const batch = writeBatch(db);

      querySnapshot.docs.forEach((doc) => {
        batch.update(doc.ref, { read: true });
      });

      await batch.commit();
    } catch (error) {      throw new Error('Failed to mark all notifications as read');
    }
  }

  // Real-time listeners
  static subscribeToSessionComments(sessionId: string, callback: (comments: Comment[]) => void, clientKey?: string): () => void {
    const key = clientKey || clientKeyManager['cachedClientKey'] || 'athleto';
    const q = query(
      CommentsRef_Collection(key),
      where('session_id', '==', sessionId),
      orderBy('created_at', 'asc')
    );

    return onSnapshot(
      q,
      (querySnapshot) => {
        const comments = querySnapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            ...data,
            id: doc.id,
            created_at: toDate(data.created_at),
            updated_at: toDate(data.updated_at),
          } as Comment;
        });
        callback(comments);
      },
      (error) => {        callback([]);
      }
    );
  }

  static subscribeToUserNotifications(userId: string, callback: (notifications: Notification[]) => void): () => void {
    const q = query(
      UserNotificationsRef_Collection(userId),
      orderBy('created_at', 'desc'),
      limit(20)
    );
    return onSnapshot(
      q,
      (querySnapshot) => {        const notifications = querySnapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            ...data,
            id: doc.id,
            created_at: toDate(data.created_at),
          } as Notification;
        });
        callback(notifications);
      },
      (error) => {        // Return empty notifications on error to prevent crashes
        callback([]);
      }
    );
  }

  static async joinTeamByInviteCode(
    inviteCode: string,
    userId: string
  ): Promise<{ outcome: 'linked' | 'pending'; team?: Team; requestId?: string; teamId?: string }> {
    try {
      const acceptInvite = httpsCallable(functions, 'acceptInviteWithPolicy');
      const result = await acceptInvite({
        inviteType: 'team',
        code: inviteCode,
        idempotencyKey: `team-${userId}-${inviteCode}`,
      });

      const data = result.data as { outcome: 'linked' | 'pending'; teamId?: string; requestId?: string };
      if (data.outcome === 'pending') {
        return {
          outcome: 'pending',
          requestId: data.requestId,
          teamId: data.teamId,
        };
      }

      if (!data.teamId) {
        throw new Error('Invite accepted but no team ID returned');
      }

      const team = await this.getTeam(data.teamId);
      if (!team) {
        throw new Error('Team not found after invite acceptance');
      }
      return { outcome: 'linked', team, teamId: data.teamId };
    } catch (error) {      throw error;
    }
  }


  static async searchAthletes(searchTerm: string, currentUserId: string, limitCount: number = 20): Promise<any[]> {
    try {
      const clientKey = await clientKeyManager.getClientKey();

      const searchLower = searchTerm.toLowerCase();

      // Get all users (not just athletes) for better search coverage
      const q = query(
        UsersRef_Collection(clientKey),
        limit(200) // Limit to prevent loading too many users
      );
      
      const querySnapshot = await getDocs(q);
      const users = querySnapshot.docs
        .map((doc) => ({
          ...doc.data(),
          id: doc.id,
          created_at: toDate(doc.data().created_at),
          updated_at: toDate(doc.data().updated_at),
        }))
        .filter((user: any) => user.id !== currentUserId) // Exclude current user
        .map((user: any) => {
          // Calculate search relevance score
          let relevanceScore = 0;
          const displayName = user.display_name?.toLowerCase() || '';
          const email = user.email?.toLowerCase() || '';
          const sport = user.metadata?.sport?.toLowerCase() || '';
          const team = user.metadata?.team?.toLowerCase() || '';
          const location = user.metadata?.location?.toLowerCase() || '';
          const bio = user.metadata?.bio?.toLowerCase() || '';
          
          // Exact matches get highest score
          if (displayName === searchLower) relevanceScore += 100;
          else if (displayName.startsWith(searchLower)) relevanceScore += 80;
          else if (displayName.includes(searchLower)) relevanceScore += 60;
          
          if (email === searchLower) relevanceScore += 90;
          else if (email.startsWith(searchLower)) relevanceScore += 70;
          else if (email.includes(searchLower)) relevanceScore += 50;
          
          // Sport matches
          if (sport === searchLower) relevanceScore += 40;
          else if (sport.includes(searchLower)) relevanceScore += 20;
          
          // Team matches
          if (team === searchLower) relevanceScore += 30;
          else if (team.includes(searchLower)) relevanceScore += 15;
          
          // Location matches
          if (location.includes(searchLower)) relevanceScore += 10;
          
          // Bio matches
          if (bio.includes(searchLower)) relevanceScore += 5;
          
          return { ...user, relevanceScore };
        })
        .filter((user: any) => user.relevanceScore > 0) // Only include matches
        .sort((a: any, b: any) => {
          // Sort by relevance score first, then by name
          if (b.relevanceScore !== a.relevanceScore) {
            return b.relevanceScore - a.relevanceScore;
          }
          return (a.display_name || a.email || '').localeCompare(b.display_name || b.email || '');
        })
        .slice(0, limitCount);
      
      return users;
    } catch (error) {      throw new Error('Failed to search athletes');
    }
  }

  // Organization-scoped team methods
  static async getOrganizationTeams(organizationId: string, clientKey?: string): Promise<Team[]> {
    try {
      clientKey = clientKey ?? await clientKeyManager.getClientKey();

      const querySnapshot = await getDocs(OrganizationTeamsRef_Query(organizationId, clientKey));
      return querySnapshot.docs.map((doc) => {
        const data = doc.data() as any;
        return {
          ...data,
          id: doc.id,
          created_at: toDate(data.created_at),
          updated_at: toDate(data.updated_at),
        } as Team;
      });
    } catch (error) {      throw new Error('Failed to get organization teams');
    }
  }

  static async getPublicTeams(clientKey?: string): Promise<Team[]> {
    try {
      clientKey = clientKey ?? await clientKeyManager.getClientKey();

      const q = query(
        TeamsRef_Collection(clientKey),
        where('scope', '==', 'public'),
        where('is_private', '==', false),
        orderBy('created_at', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) => {
        const data = doc.data() as any;
        return {
          ...data,
          id: doc.id,
          created_at: toDate(data.created_at),
          updated_at: toDate(data.updated_at),
        } as Team;
      });
    } catch (error) {      throw new Error('Failed to get public teams');
    }
  }

  static async getGroupTeams(clientKey?: string): Promise<Team[]> {
    try {
      clientKey = clientKey ?? await clientKeyManager.getClientKey();

      const q = query(
        TeamsRef_Collection(clientKey),
        where('scope', '==', 'group'),
        where('is_private', '==', false),
        orderBy('created_at', 'desc')
      );

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) => {
        const data = doc.data() as any;
        return {
          ...data,
          id: doc.id,
          created_at: toDate(data.created_at),
          updated_at: toDate(data.updated_at),
        } as Team;
      });
    } catch (error) {      throw new Error('Failed to get group teams');
    }
  }

  static async getUserScopedTeams(userId: string, organizationId?: string): Promise<Team[]> {
    try {

      const teams: Team[] = [];

      // Get user's teams (where they are members)
      const userTeams = await this.getUserTeams(userId);
      teams.push(...userTeams);

      // Get public teams
      const publicTeams = await this.getPublicTeams();
      teams.push(...publicTeams);

      // Get group teams (free community teams)
      const groupTeams = await this.getGroupTeams();
      teams.push(...groupTeams);

      // Get organization teams if user belongs to one
      if (organizationId) {
        const orgTeams = await this.getOrganizationTeams(organizationId);
        teams.push(...orgTeams);
      }

      // Remove duplicates based on team ID
      const uniqueTeams = teams.filter((team, index, self) =>
        index === self.findIndex(t => t.id === team.id)
      );

      // Sort by creation date
      return uniqueTeams.sort((a, b) => b.created_at.getTime() - a.created_at.getTime());
    } catch (error) {      throw new Error('Failed to get user scoped teams');
    }
  }

  static async createOrganizationTeam(
    teamData: Omit<Team, 'id' | 'created_at' | 'updated_at'>,
    organizationId: string
  ): Promise<Team> {
    try {

      const orgTeamData = {
        ...teamData,
        organization_id: organizationId,
        scope: 'organization' as const,
      };

      return await this.createTeam(orgTeamData);
    } catch (error) {      throw new Error('Failed to create organization team');
    }
  }

  static async createGroupTeam(
    teamData: Omit<Team, 'id' | 'created_at' | 'updated_at'>
  ): Promise<Team> {
    try {

      const groupTeamData = {
        ...teamData,
        scope: 'group' as const,
        is_private: false, // Group teams are always public
      };

      return await this.createTeam(groupTeamData);
    } catch (error) {      throw new Error('Failed to create group team');
    }
  }

  static async canUserJoinTeam(teamId: string, userId: string, organizationId?: string): Promise<boolean> {
    try {

      const team = await this.getTeam(teamId);
      if (!team) return false;

      // Check if user is already a member
      if (team.members.includes(userId)) return false;

      // Check scope-based access
      switch (team.scope) {
        case 'public':
          return !team.is_private;

        case 'group':
          return !team.is_private; // Group teams are open to all

        case 'organization':
          // Must be part of the same organization
          return team.organization_id === organizationId;

        default:
          // Legacy teams without scope - check privacy settings
          return !team.is_private;
      }
    } catch (error) {      return false;
    }
  }

  static async getTeamsByScope(scope: 'public' | 'organization' | 'group', organizationId?: string): Promise<Team[]> {
    try {

      let q;

      switch (scope) {
        case 'public':
          q = query(
            TeamsRef_Collection(await clientKeyManager.getClientKey()),
            where('scope', '==', 'public'),
            where('is_private', '==', false),
            orderBy('created_at', 'desc')
          );
          break;

        case 'organization':
          if (!organizationId) throw new Error('Organization ID required for organization scope');
          q = query(
            TeamsRef_Collection(await clientKeyManager.getClientKey()),
            where('scope', '==', 'organization'),
            where('organization_id', '==', organizationId),
            orderBy('created_at', 'desc')
          );
          break;

        case 'group':
          q = query(
            TeamsRef_Collection(await clientKeyManager.getClientKey()),
            where('scope', '==', 'group'),
            where('is_private', '==', false),
            orderBy('created_at', 'desc')
          );
          break;

        default:
          throw new Error('Invalid scope');
      }

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) => {
        const data = doc.data() as any;
        return {
          ...data,
          id: doc.id,
          created_at: toDate(data.created_at),
          updated_at: toDate(data.updated_at),
        } as Team;
      });
    } catch (error) {      throw new Error('Failed to get teams by scope');
    }
  }

  // Video Library Management

  // Helper to extract YouTube video ID from various URL formats
  private static extractYouTubeVideoId(url: string): string | null {
    const patterns = [
      // Standard YouTube URLs: youtube.com/watch?v=, youtu.be/, youtube.com/embed/
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      // YouTube Shorts URLs: youtube.com/shorts/VIDEO_ID
      /youtube\.com\/shorts\/([^&\n?#]+)/,
      // Direct video ID (11 characters)
      /^([a-zA-Z0-9_-]{11})$/
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }
    return null;
  }

  // Category Management
  static async createVideoCategory(teamId: string, categoryData: Omit<VideoCategory, 'id' | 'created_at' | 'updated_at' | 'video_count' | 'team_id'>, clientKey: string = 'athleto', team_coach_ids: string[] = [], team_manager_ids: string[] = []): Promise<VideoCategory> {
    try {

      const categoriesCollection = collection(db, 'clients', clientKey, 'teams', teamId, 'video_categories');
      const categoryDocRef = doc(categoriesCollection);
      const categoryId = categoryDocRef.id;

      const newCategoryData: any = {
        ...categoryData,
        id: categoryId,
        team_id: teamId,
        team_coach_ids,
        team_manager_ids,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
        video_count: 0,
      };

      // Remove undefined values (Firestore doesn't accept undefined)
      Object.keys(newCategoryData).forEach(key => {
        if (newCategoryData[key] === undefined) {
          delete newCategoryData[key];
        }
      });

      await setDoc(categoryDocRef, newCategoryData);

      const docSnap = await getDoc(categoryDocRef);
      if (!docSnap.exists()) {
        throw new Error('Category creation failed');
      }

      const data = docSnap.data();
      return {
        ...data,
        id: categoryId,
        created_at: toDate(data.created_at),
        updated_at: toDate(data.updated_at),
      } as VideoCategory;
    } catch (error) {      throw new Error('Failed to create video category');
    }
  }

  static async getTeamCategories(teamId: string, parentCategoryId?: string | null, clientKey: string = 'athleto'): Promise<VideoCategory[]> {
    try {

      let q;
      if (parentCategoryId === undefined) {
        // Get all categories for the team
        q = query(
          collection(db, 'clients', clientKey, 'teams', teamId, 'video_categories'),
          orderBy('order', 'asc'),
          orderBy('created_at', 'desc')
        );
      } else if (parentCategoryId === null) {
        // Get only top-level categories (no parent)
        q = query(
          collection(db, 'clients', clientKey, 'teams', teamId, 'video_categories'),
          where('parent_category_id', '==', null),
          orderBy('order', 'asc'),
          orderBy('created_at', 'desc')
        );
      } else {
        // Get subcategories of a specific parent
        q = query(
          collection(db, 'clients', clientKey, 'teams', teamId, 'video_categories'),
          where('parent_category_id', '==', parentCategoryId),
          orderBy('order', 'asc'),
          orderBy('created_at', 'desc')
        );
      }

      const querySnapshot = await getDocs(q);      return querySnapshot.docs.map((doc) => {
        const data = doc.data() as any;
        return {
          ...data,
          id: doc.id,
          created_at: toDate(data.created_at),
          updated_at: toDate(data.updated_at),
        } as VideoCategory;
      });
    } catch (error) {      throw new Error('Failed to get team categories');
    }
  }

  static async updateVideoCategory(teamId: string, categoryId: string, updates: Partial<VideoCategory>, clientKey: string = 'athleto'): Promise<void> {
    try {

      const categoryRef = doc(db, 'clients', clientKey, 'teams', teamId, 'video_categories', categoryId);
      await updateDoc(categoryRef, {
        ...updates,
        updated_at: serverTimestamp(),
      });
    } catch (error) {      throw new Error('Failed to update video category');
    }
  }

  static async deleteVideoCategory(teamId: string, categoryId: string, clientKey: string = 'athleto'): Promise<void> {
    try {

      // Delete all videos in this category first
      const videosQuery = query(
        collection(db, 'clients', clientKey, 'teams', teamId, 'videos'),
        where('category_id', '==', categoryId)
      );
      const videosSnapshot = await getDocs(videosQuery);

      const batch = writeBatch(db);
      videosSnapshot.docs.forEach((doc) => {
        batch.delete(doc.ref);
      });

      // Delete subcategories
      const subcategoriesQuery = query(
        collection(db, 'clients', clientKey, 'teams', teamId, 'video_categories'),
        where('parent_category_id', '==', categoryId)
      );
      const subcategoriesSnapshot = await getDocs(subcategoriesQuery);

      subcategoriesSnapshot.docs.forEach((doc) => {
        batch.delete(doc.ref);
      });

      // Delete the category itself
      batch.delete(doc(db, 'clients', clientKey, 'teams', teamId, 'video_categories', categoryId));

      await batch.commit();
    } catch (error) {      throw new Error('Failed to delete video category');
    }
  }

  // Video Management
  static async createTeamVideo(teamId: string, videoData: Omit<TeamVideo, 'id' | 'posted_at' | 'youtube_video_id' | 'thumbnail_url' | 'views' | 'likes' | 'team_id'>, clientKey: string = 'athleto', team_coach_ids: string[] = [], team_manager_ids: string[] = []): Promise<TeamVideo> {
    try {

      const videoId = this.extractYouTubeVideoId(videoData.youtube_url);
      if (!videoId) {
        throw new Error('Invalid YouTube URL');
      }

      // Detect if this is a YouTube Short
      const isShort = /youtube\.com\/shorts\//.test(videoData.youtube_url);

      const videosCollection = collection(db, 'clients', clientKey, 'teams', teamId, 'videos');
      const videoDocRef = doc(videosCollection);
      const docId = videoDocRef.id;

      // Generate YouTube thumbnail URL
      const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;

      const newVideoData: any = {
        ...videoData,
        id: docId,
        team_id: teamId,
        team_coach_ids,
        team_manager_ids,
        youtube_video_id: videoId,
        thumbnail_url: thumbnailUrl,
        is_short: isShort,
        posted_at: serverTimestamp(),
        views: 0,
        likes: [],
      };

      // Remove undefined values (Firestore doesn't accept undefined)
      Object.keys(newVideoData).forEach(key => {
        if (newVideoData[key] === undefined) {
          delete newVideoData[key];
        }
      });

      await setDoc(videoDocRef, newVideoData);

      // Increment video count in category
      const categoryRef = doc(db, 'clients', clientKey, 'teams', teamId, 'video_categories', videoData.category_id);
      await updateDoc(categoryRef, {
        video_count: increment(1),
      });

      const docSnap = await getDoc(videoDocRef);
      if (!docSnap.exists()) {
        throw new Error('Video creation failed');
      }

      const data = docSnap.data();
      return {
        ...data,
        id: docId,
        posted_at: toDate(data.posted_at),
      } as TeamVideo;
    } catch (error) {      throw new Error('Failed to create team video');
    }
  }

  static async getTeamVideos(teamId: string, categoryId?: string, clientKey: string = 'athleto'): Promise<TeamVideo[]> {
    try {

      let q;
      if (categoryId) {
        q = query(
          collection(db, 'clients', clientKey, 'teams', teamId, 'videos'),
          where('category_id', '==', categoryId),
          orderBy('posted_at', 'desc')
        );
      } else {
        q = query(
          collection(db, 'clients', clientKey, 'teams', teamId, 'videos'),
          orderBy('posted_at', 'desc')
        );
      }

      const querySnapshot = await getDocs(q);      return querySnapshot.docs.map((doc) => {
        const data = doc.data() as any;
        return {
          ...data,
          id: doc.id,
          posted_at: toDate(data.posted_at),
        } as TeamVideo;
      });
    } catch (error) {      throw new Error('Failed to get team videos');
    }
  }

  static async updateTeamVideo(teamId: string, videoId: string, updates: Partial<TeamVideo>, clientKey: string = 'athleto'): Promise<void> {
    try {
      const videoRef = doc(db, 'clients', clientKey, 'teams', teamId, 'videos', videoId);

      // Remove undefined values (Firestore doesn't accept undefined)
      const cleanedUpdates: Record<string, any> = {};
      Object.entries(updates).forEach(([key, value]) => {
        if (value !== undefined) {
          cleanedUpdates[key] = value;
        }
      });

      await updateDoc(videoRef, cleanedUpdates);
    } catch (error) {      throw new Error('Failed to update team video');
    }
  }

  static async deleteTeamVideo(teamId: string, videoId: string, categoryId: string | null | undefined, clientKey: string = 'athleto'): Promise<void> {
    try {
      await deleteDoc(doc(db, 'clients', clientKey, 'teams', teamId, 'videos', videoId));

      // Only decrement video count if video had a category
      if (categoryId) {
        try {
          const categoryRef = doc(db, 'clients', clientKey, 'teams', teamId, 'video_categories', categoryId);
          await updateDoc(categoryRef, {
            video_count: increment(-1),
          });
        } catch (catError) {        }
      }
    } catch (error) {      throw new Error('Failed to delete team video');
    }
  }

  static async incrementVideoViews(teamId: string, videoId: string, clientKey: string = 'athleto'): Promise<void> {
    try {

      const videoRef = doc(db, 'clients', clientKey, 'teams', teamId, 'videos', videoId);
      await updateDoc(videoRef, {
        views: increment(1),
      });
    } catch (error) {      // Don't throw - views are not critical
    }
  }

  static async toggleVideoLike(teamId: string, videoId: string, userId: string, clientKey: string = 'athleto'): Promise<void> {
    try {

      const videoRef = doc(db, 'clients', clientKey, 'teams', teamId, 'videos', videoId);
      const videoDoc = await getDoc(videoRef);

      if (!videoDoc.exists()) {
        throw new Error('Video not found');
      }

      const videoData = videoDoc.data() as TeamVideo;
      const likes = videoData.likes || [];
      const isLiked = likes.includes(userId);

      if (isLiked) {
        // Remove like
        await updateDoc(videoRef, {
          likes: likes.filter(id => id !== userId),
        });
      } else {
        // Add like
        await updateDoc(videoRef, {
          likes: [...likes, userId],
        });
      }
    } catch (error) {      throw new Error('Failed to toggle video like');
    }
  }

  // Follow/Unfollow System
  static async followUser(followerId: string, followingId: string): Promise<void> {
    try {
      const clientKey = await clientKeyManager.getClientKey();

      // Create a follow document
      const followId = doc(FollowsRef_Collection(clientKey)).id;
      const followRef = FollowRef_Document(followId, clientKey);

      await setDoc(followRef, {
        id: followId,
        follower_id: followerId,
        following_id: followingId,
        created_at: serverTimestamp(),
      });

      // Optionally create a notification for the user being followed
      await this.createNotification({
        user_id: followingId,
        type: 'follow',
        title: 'New Follower',
        message: 'Someone started following you',
        read: false,
        related_id: followerId,
        related_type: 'user',
      });
    } catch (error) {      throw new Error('Failed to follow user');
    }
  }

  // ============================================================================
  // DEMO PLAYER MANAGEMENT (via Cloud Functions)
  // ============================================================================

  /**
   * Generate demo players for a team
   * Calls Cloud Function to create fake user accounts
   * @param gender - 'male' or 'female' to determine which names to use
   */
  static async generateDemoPlayers(
    teamId: string,
    count: number = 10,
    clientKey?: string,
    gender: 'male' | 'female' = 'female'
  ): Promise<{ success: boolean; playerIds: string[]; error?: string }> {
    try {
      clientKey = clientKey ?? await clientKeyManager.getClientKey();

      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();
      const generateDemoPlayersFn = httpsCallable(functions, 'generateDemoPlayers');

      const result = await generateDemoPlayersFn({ teamId, count, clientKey, gender });
      const data = result.data as { success: boolean; playerIds: string[]; message: string };      return { success: data.success, playerIds: data.playerIds };
    } catch (error: any) {      const errorMessage = error.message || 'Failed to generate demo players';
      return { success: false, playerIds: [], error: errorMessage };
    }
  }

  /**
   * Remove all demo players from a team
   * Calls Cloud Function to delete demo user accounts
   */
  static async removeDemoPlayers(
    teamId: string,
    clientKey?: string
  ): Promise<{ success: boolean; removedCount: number; error?: string }> {
    try {
      clientKey = clientKey ?? await clientKeyManager.getClientKey();

      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();
      const removeDemoPlayersFn = httpsCallable(functions, 'removeDemoPlayers');

      const result = await removeDemoPlayersFn({ teamId, clientKey });
      const data = result.data as { success: boolean; removedCount: number; message: string };      return { success: data.success, removedCount: data.removedCount };
    } catch (error: any) {      const errorMessage = error.message || 'Failed to remove demo players';
      return { success: false, removedCount: 0, error: errorMessage };
    }
  }

  /**
   * Check if a team has any demo players
   * Calls Cloud Function to check
   */
  static async hasDemoPlayers(
    teamId: string,
    clientKey?: string
  ): Promise<boolean> {
    try {
      clientKey = clientKey ?? await clientKeyManager.getClientKey();

      const { getFunctions, httpsCallable } = await import('firebase/functions');
      const functions = getFunctions();
      const hasDemoPlayersFn = httpsCallable(functions, 'hasDemoPlayers');

      const result = await hasDemoPlayersFn({ teamId, clientKey });
      const data = result.data as { hasDemoPlayers: boolean };

      return data.hasDemoPlayers;
    } catch (error) {      return false;
    }
  }

  static async unfollowUser(followerId: string, followingId: string): Promise<void> {
    try {
      const clientKey = await clientKeyManager.getClientKey();

      // Find the follow document
      const q = query(
        FollowsRef_Collection(clientKey),
        where('follower_id', '==', followerId),
        where('following_id', '==', followingId)
      );

      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        // Delete all matching follow documents (should only be one)
        const batch = writeBatch(db);
        querySnapshot.docs.forEach((doc) => {
          batch.delete(doc.ref);
        });
        await batch.commit();
      }
    } catch (error) {      throw new Error('Failed to unfollow user');
    }
  }

  static async getFollowing(userId: string): Promise<Follow[]> {
    try {
      const clientKey = await clientKeyManager.getClientKey();

      const q = UserFollowingRef_Query(userId, undefined, clientKey);
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          ...data,
          id: doc.id,
          created_at: toDate(data.created_at),
        } as Follow;
      });
    } catch (error) {      throw new Error('Failed to get following list');
    }
  }

  static async getFollowers(userId: string): Promise<Follow[]> {
    try {
      const clientKey = await clientKeyManager.getClientKey();

      const q = UserFollowersRef_Query(userId, undefined, clientKey);
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          ...data,
          id: doc.id,
          created_at: toDate(data.created_at),
        } as Follow;
      });
    } catch (error) {      throw new Error('Failed to get followers list');
    }
  }

  // Comment System
  static async getSessionComments(sessionId: string, clientKey?: string): Promise<Comment[]> {
    try {
      clientKey = clientKey ?? await clientKeyManager.getClientKey();

      const q = SessionCommentsRef_Query(sessionId, undefined, clientKey);
      const querySnapshot = await getDocs(q);

      return querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          ...data,
          id: doc.id,
          created_at: toDate(data.created_at),
          updated_at: data.updated_at ? toDate(data.updated_at) : toDate(data.created_at),
        } as Comment;
      });
    } catch (error) {      throw new Error('Failed to get session comments');
    }
  }

  static async addComment(commentData: Omit<Comment, 'id' | 'created_at' | 'updated_at'>): Promise<Comment> {
    try {
      const clientKey = await clientKeyManager.getClientKey();

      const commentId = doc(CommentsRef_Collection(clientKey)).id;
      const commentRef = CommentRef_Document(commentId, clientKey);

      const newComment = {
        ...commentData,
        id: commentId,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      };

      await setDoc(commentRef, newComment);

      // Read back the document to get actual Firestore timestamps
      const docSnap = await getDoc(commentRef);
      if (!docSnap.exists()) {
        throw new Error('Comment creation failed');
      }

      const data = docSnap.data();
      return {
        ...data,
        id: commentId,
        created_at: toDate(data.created_at),
        updated_at: toDate(data.updated_at),
      } as Comment;
    } catch (error) {      throw new Error('Failed to add comment');
    }
  }

  // Team Invite Code Management
  static async regenerateTeamInviteCode(teamId: string, clientKey?: string): Promise<string> {
    try {
      clientKey = clientKey ?? await clientKeyManager.getClientKey();

      const newInviteCode = this.generateInviteCode();

      const q = query(
        TeamsRef_Collection(clientKey),
        where('id', '==', teamId)
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        throw new Error('Team not found');
      }

      const teamRef = querySnapshot.docs[0].ref;
      await updateDoc(teamRef, {
        invite_code: newInviteCode,
        updated_at: serverTimestamp(),
      });

      return newInviteCode;
    } catch (error) {      throw new Error('Failed to regenerate team invite code');
    }
  }

  // Utility methods
  private static generateInviteCode(): string {
    return Math.random().toString(36).substring(2, 8).toUpperCase();
  }
}
